'use client';

import { Dispatch, ReactNode, SetStateAction, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { SidebarLink } from '..';
import clsx from 'clsx';
import { BsBalloonHeart } from 'react-icons/bs';
import { LuGraduationCap } from 'react-icons/lu';

import { Button, Icons, Modal } from '@pickatale/ui-kit';

import { ThemeSwitcher } from '../ThemeManager/ThemeSwitcher';

import styles from './SideBar.module.scss';

import PhonicsLogo from '../../assets/phonics-logo-small.png';

type SideBarProps = {
  user: {
    name?: string;
    identifier?: string;
  };
  className?: string;
  fullHide?: boolean;
  footerItems?: ReactNode;
  showBookCreator?: boolean;
  showBookCreatorKids?: boolean;
  showPhonics?: boolean;
  showDashboard?: boolean;
  showAcademy?: boolean;
  isStudent?: boolean;
  isConsumer?: boolean;
  logoutPath?: string;
  avatar?: ReactNode;
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  t: (key: string, options: { _key: string; _tags?: string[] }) => string;
};

export const SideBar = ({
  user,
  className,
  fullHide,
  footerItems,
  showDashboard,
  showPhonics,
  showBookCreator,
  showBookCreatorKids,
  showAcademy = false,
  isStudent = false,
  isConsumer = false,
  logoutPath = '/logout',
  avatar,
  isExpanded,
  setIsExpanded,
  t,
}: SideBarProps) => {
  const [isOpened, setIsOpened] = useState(false);
  const initials = user.name?.charAt(0);

  const isPhonics = window.location.pathname.startsWith('/phonics');
  const isCreateKids = window.location.pathname.startsWith('/kids');
  const isNotLibrary = isPhonics || isCreateKids;

  return (
    <>
      <div className={clsx(styles.header, 'pt-sidebar-collapsed')}>
        <button
          className={styles['burger-button']}
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
          aria-label={isExpanded ? 'close menu' : 'open menu'}
          type="button"
        >
          <Icons.Burger />
        </button>
      </div>
      {isExpanded && <div className={styles.overlay} onClick={() => setIsExpanded(false)} aria-hidden="true" />}
      <div
        className={clsx(
          styles.container,
          {
            [styles.collapsed]: !isExpanded,
            [styles['full-hide']]: fullHide && !isExpanded,
            'pt-sidebar-expanded': isExpanded,
            'pt-sidebar-collapsed': !isExpanded,
          },
          className,
        )}
      >
        <div className={styles.profile}>
          {avatar ? avatar : <div className={styles.avatar}>{initials}</div>}
          <span className={clsx(styles['full-name'])}>{user.name}</span>
          <span className={clsx(styles.email)}>{user.identifier}</span>
        </div>

        <div className={styles['links-container']} style={{ flexGrow: 1 }}>
          <SidebarLink href="/library" icon={Icons.Home} as={isNotLibrary ? 'a' : Link}>
            {t('Library', { _key: 'library' })}
          </SidebarLink>
          <SidebarLink href={'/library/search'} icon={Icons.Search} as={isNotLibrary ? 'a' : Link}>
            {t('Search', { _key: 'topbar.search' })}
          </SidebarLink>
          {showDashboard && (
            <>
              <SidebarLink href={'/students'} icon={Icons.MyClasses} as={isNotLibrary ? 'a' : Link}>
                {t('My classes', { _key: 'my_classes' })}
              </SidebarLink>
              <SidebarLink href={'/statistics'} icon={Icons.Statistics} as={isNotLibrary ? 'a' : Link}>
                {t('Statistics', { _key: 'statistics' })}
              </SidebarLink>
            </>
          )}
          {!isStudent && !isConsumer && showBookCreator && (
            <SidebarLink href={'/book-creator/create'} icon={Icons.MyBooks} as={isNotLibrary ? 'a' : Link}>
              {t('Book creator', { _key: 'book_creator' })}
            </SidebarLink>
          )}
          {(isStudent || isConsumer) && showBookCreatorKids && (
            <SidebarLink href={'/book-creator-kids/create'} icon={Icons.Sparkles} as={isNotLibrary ? 'a' : Link}>
              {t('Story maker', { _key: 'story_maker' })}
            </SidebarLink>
          )}
          {showPhonics && (
            <SidebarLink
              href={isPhonics ? '/' : '/phonics'}
              icon={Icons.Phonics}
              supressIcon="open"
              as={isPhonics ? Link : 'a'}
            >
              <Image
                src={PhonicsLogo}
                alt="Pickatale Phonics Logo"
                width="15605"
                height="4846"
                className={styles.image}
              />
            </SidebarLink>
          )}
          {showAcademy && (
            <SidebarLink href="/academy" icon={LuGraduationCap} as={isNotLibrary ? 'a' : Link}>
              {t('Academy', { _key: 'academy' })}
            </SidebarLink>
          )}
        </div>
        <div className={clsx(styles['links-container'])}>
          <div className={clsx(styles['theme-switcher'], { [styles['theme-collapsed']]: !isExpanded })}>
            <ThemeSwitcher isExpanded={isExpanded} t={t} />
          </div>

          <div>
            <Button className={clsx(styles['logout-modal-button'])} variant={'text'} onClick={() => setIsOpened(true)}>
              <Icons.LogOut />
              {isExpanded && t('Logout', { _key: 'logout.text' })}
            </Button>
            <Modal
              header={t('Log Out Confirmation', { _key: 'log_out_confirmation' })}
              isOpened={isOpened}
              onClose={() => setIsOpened(false)}
            >
              <div className={clsx(styles['logout-container'])}>
                {t('Are you sure you want to log out?', { _key: 'logout.confirm' })}
                <div className={clsx(styles['logout-buttons'])}>
                  <Button variant={'outlined'} onClick={() => setIsOpened(false)}>
                    {t('Cancel', { _key: 'cancel' })}
                  </Button>
                  <Button>
                    <Link href={logoutPath} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {t('Log out', { _key: 'log_out' })}
                    </Link>
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
          {footerItems}
        </div>
      </div>
    </>
  );
};
